import { createContext, useContext, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { DocHead } from '../../components/DocBlocks';
import type { Lang } from '../../snippets';
import { PAGES, docPath } from './registry';

export type Modality = 'text' | 'image' | 'video' | 'embedding';

export interface CatalogModel {
  id: string;
  owned_by: string;
  modality?: Modality;
  display_name?: string;
  provider?: string;
  family?: string;
  context_window?: number;
  /** Dimensions for embeddings. */
  max_output_tokens?: number;
  capabilities?: { stream?: boolean; tools?: boolean; vision?: boolean; reasoning?: boolean; json_schema?: boolean };
  pricing?: {
    input_per_1m?: number;
    output_per_1m?: number;
    per_image?: number;
    per_1m_video_tokens?: number;
    per_second_720p?: number;
  };
}

export interface DocsState {
  publicView: boolean;
  lang: Lang;
  setLang: (lang: Lang) => void;
  models: CatalogModel[] | null;
  catalogError: string | null;
  text?: CatalogModel;
  image?: CatalogModel;
  video?: CatalogModel;
  /** Example model ids. */
  model: string;
  imageModel: string;
  videoModel: string;
}

export const DocsContext = createContext<DocsState | null>(null);

export function useDocs(): DocsState {
  const state = useContext(DocsContext);
  if (!state) throw new Error('useDocs outside Docs');
  return state;
}

/** Bold text when public. */
export function AppLink({ to, children }: { to: string; children: ReactNode }) {
  const { publicView } = useDocs();
  return publicView ? <b>{children}</b> : <Link to={to}>{children}</Link>;
}

export function DocLink({ slug, hash, children }: { slug: string; hash?: string; children?: ReactNode }) {
  const page = PAGES.find((p) => p.slug === slug);
  return <Link to={`${docPath(slug)}${hash ? `#${hash}` : ''}`}>{children ?? page?.title ?? slug}</Link>;
}

/** Head from registry. */
export function PageHead({ slug, children }: { slug: string; children: ReactNode }) {
  const page = PAGES.find((p) => p.slug === slug) ?? PAGES[0];
  return (
    <DocHead group={page.group} title={page.title}>
      <p className="docs-summary">{children}</p>
    </DocHead>
  );
}

/** Same rule as useExampleModel. */
export function pick(models: CatalogModel[] | null, modality: Modality, fallback: string): CatalogModel | undefined {
  if (!models) return undefined;
  const ofKind = models.filter((m) => (m.modality ?? 'text') === modality);
  return ofKind.find((m) => m.id === fallback) ?? ofKind.find((m) => m.owned_by !== 'mock') ?? ofKind[0];
}

function inline(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(inline).join(', ')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.entries(value)
      .map(([k, v]) => `${JSON.stringify(k)}: ${inline(v)}`)
      .join(', ')}}`;
  }
  return JSON.stringify(value);
}

/** JSON, short objects inline. */
export function pretty(value: unknown, indent = '', lead = 0): string {
  const one = inline(value);
  if (typeof value !== 'object' || value === null || indent.length + lead + one.length <= 72) return one;
  const next = `${indent}  `;
  if (Array.isArray(value)) return `[\n${value.map((v) => next + pretty(v, next)).join(',\n')}\n${indent}]`;
  const rows = Object.entries(value).map(([k, v]) => `${next}${JSON.stringify(k)}: ${pretty(v, next, k.length + 4)}`);
  return `{\n${rows.join(',\n')}\n${indent}}`;
}

export const round2 = (n: number) => Math.round(n * 100) / 100;
